import { Injectable } from '@angular/core';
import { LocalizeRouterService } from '@gilsdav/ngx-translate-router';
import { SkihirePoiInterface } from '../../shared/interfaces/skihire-poi-interface';
import { Router, Params } from '@angular/router';
import { PlatformInformationService } from '../../../../projects/platform-manager/src/public_api';
import { DatePipe, ViewportScroller } from '@angular/common';
import { BookingParametersInterface, codetype } from '../../shared/entities/bookingParameters';
import { PartnerLinkService } from './partner-link.service';

@Injectable({
  providedIn: 'root'
})
export class NavigationService {
  constructor(
    protected localizedRouterService: LocalizeRouterService,
    protected router: Router,
    protected platformService: PlatformInformationService,
    protected scrollService: ViewportScroller,
    protected partnerLinkService: PartnerLinkService
  ) {}

  private openLinkInNewWindow(url: string) {
    const win = window.open(url, '_blank');
    if (!win) {
      console.warn(`The url ${url} cannot be open in a new window, so it will be opened in the same window.`);
      window.top.location.href = url;
    }
  }

  public navigateToPoi(
    poi: SkihirePoiInterface,
    scrollToTop = false,
    partner = '',
    windowNavigation = false,
    params: Params = []
  ) {
    const parts = poi.url.split('/');
    let slug: string;
    switch (poi.type) {
      case 'AlpinTown': {
        slug = parts[3] + '/' + parts[4] + '/' + parts[5];
        if (partner) {
          slug = partner + '/' + slug;
        }
        const route = this.localizedRouterService.translateRoute('/SKIHIREENTRY/' + slug) as string;
        if (windowNavigation) {
          const url = this.router
            .createUrlTree([route], {
              queryParams: params
            })
            .toString();
          if (params?.redirect === 'newTab') {
            this.openLinkInNewWindow(url);
          } else {
            window.top.location.href = url;
          }
        } else {
          // prevent "same page" routing. e.g. sideBar returning customer formsubmit
          if (!this.router.isActive(route, false)) {
            // navigate primary route and keep existing query parameters
            this.router.navigate([route], { queryParams: params, queryParamsHandling: 'merge' });
          }
        }
        break;
      }
      case 'PointOfInterest': {
        slug = parts[parts.length - 1];
        if (partner) {
          slug = partner + '/' + slug;
        }
        const route = this.localizedRouterService.translateRoute('/SKIHIREENTRY/poi/' + slug) as string;

        if (windowNavigation) {
          const url = this.router
            .createUrlTree([route], {
              queryParams: params
            })
            .toString();
          if (params?.redirect === 'newTab') {
            this.openLinkInNewWindow(url);
          } else {
            window.top.location.href = url;
          }
        } else {
          // prevent "same page" routing. e.g. sideBar returning customer formsubmit
          if (!this.router.isActive(route, false)) {
            // navigate primary route and keep existing query parameters
            this.router.navigate([route], { queryParams: params, queryParamsHandling: 'merge' });
          }
        }
        break;
      }
      default: {
        // 'AlpinShop
        slug =
          parts[parts.length - 4] +
          '/' +
          parts[parts.length - 3] +
          '/' +
          parts[parts.length - 2] +
          '/' +
          parts[parts.length - 1];
        const route = this.localizedRouterService.translateRoute('/SKIHIREENTRY/' + slug) as string;

        if (windowNavigation) {
          const url = this.router
            .createUrlTree([route], {
              queryParams: params
            })
            .toString();
          if (params?.redirect === 'newTab') {
            this.openLinkInNewWindow(url);
          } else {
            window.top.location.href = url;
          }
        } else {
          if (!this.router.isActive(route, false)) {
            this.router.navigate([route], { queryParams: params, queryParamsHandling: 'merge' });
          }
        }
      }
    }
    if (this.platformService.getIsBrowser() && scrollToTop) {
      this.scrollService.scrollToPosition([0, 0]);
    }
  }

  navigateToProductSelection(
    poi: SkihirePoiInterface,
    bookingParameters: BookingParametersInterface,
    params: Params = []
  ) {
    const route = this.localizedRouterService.translateRoute('/SKIHIREENTRY/SKIHIRE.PRODUCTS');
    const routeWithPartner = this.partnerLinkService.getUrlReplacingPartnerAfterPrefix(
      route,
      this.localizedRouterService.translateRoute('/SKIHIREENTRY'),
      bookingParameters.partner
    );
    const promo = bookingParameters.codes.find(code => code.type === codetype.promo);
    const datePipe = new DatePipe('en-US');
    params = {
      ...params,
      startDate: `${bookingParameters.start.getFullYear()}-${('0' + (bookingParameters.start.getMonth() + 1)).slice(-2)}-${('0' + bookingParameters.start.getDate()).slice(-2)}`, // "2020-03-24"
      endDate: `${bookingParameters.end.getFullYear()}-${('0' + (bookingParameters.end.getMonth() + 1)).slice(-2)}-${('0' + bookingParameters.end.getDate()).slice(-2)}`, // "2020-03-28"
      startPeriod: datePipe.transform(this.getLastSaturday(new Date()), 'yyyy-MM-dd'),
      promotionCode: promo ? promo.text : null,
      'personAges[]': bookingParameters.ages,
      shopId: poi.foreign_id
    };

    const url = this.router
      .createUrlTree([routeWithPartner], {
        queryParams: params
      })
      .toString();
    if (params?.redirect === 'newTab') {
      this.openLinkInNewWindow(url);
    } else {
      window.top.location.href = url;
    }
  }

  public getLastSaturday(date: Date) {
    if (date.getDay() === 6) {
      return new Date(date.getTime());
    }
    // getDay returns a number from 0 Sunday to 6 Saturday. we always have to substract days
    const dateCopy = new Date(date.getTime());
    dateCopy.setDate(date.getDate() - (date.getDay() + 1));
    return dateCopy;
  }

  /**
   * sends user to the searchpage. should use the angular router when that page gets implemented in angular.
   * @param query the searchinput of the user
   */
  public navigateToSearchPage(query: string) {
    const route =
      this.localizedRouterService.translateRoute('/SKIHIREENTRY/SKIHIRE.SEARCH') +
      '?search=' +
      encodeURIComponent(query);
    window.location.href = route;
  }
}
